import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useChat } from '@ai-sdk/react';
import { DefaultChatTransport } from 'ai';
import {
  getConversations,
  getConversation,
  deleteConversation,
  getUsage
} from '../services/aiCoachService';
import { trackUpgradePromptShown, trackUpgradePromptClick } from '../utils/analytics';
import './AIChatPanel.css';

function AIChatPanel({ coachClientId = null }) {
  const [isOpen, setIsOpen] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [usage, setUsage] = useState(null);
  const [atLimit, setAtLimit] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const activeConversationIdRef = useRef(null);
  const recognitionRef = useRef(null);

  // Web Speech API support
  const speechSupported = typeof window !== 'undefined' &&
    ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window);

  // Keep ref in sync
  useEffect(() => {
    activeConversationIdRef.current = activeConversationId;
  }, [activeConversationId]);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

  const transport = useMemo(() => new DefaultChatTransport({
    api: `${API_URL}/ai-coach/chat`,
    headers: () => {
      const token = localStorage.getItem('token');
      return token ? { Authorization: `Bearer ${token}` } : {};
    },
    body: () => ({ conversationId: activeConversationIdRef.current }),
    fetch: async (url, options) => {
      const response = await window.fetch(url, options);
      const convId = response.headers.get('x-conversation-id');
      if (convId && !activeConversationIdRef.current) {
        activeConversationIdRef.current = convId;
        setActiveConversationId(convId);
      }
      return response;
    }
  }), [API_URL]);

  const {
    messages,
    sendMessage,
    status,
    setMessages,
    error
  } = useChat({
    transport,
    onFinish: () => {
      loadUsage();
    },
    onError: (err) => {
      if (err?.message?.includes('403')) {
        setAtLimit(true);
      }
    }
  });

  const isLoading = status === 'submitted' || status === 'streaming';

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Load usage when panel opens
  const loadUsage = useCallback(async () => {
    try {
      const data = await getUsage();
      setUsage(data);
      setAtLimit(data.messagesRemaining <= 0);
    } catch (err) {
      console.error('Failed to load usage:', err);
    }
  }, []);

  // Load conversations when history is opened
  const loadConversations = useCallback(async () => {
    try {
      const data = await getConversations({ coachClientId });
      setConversations(data.conversations || []);
    } catch (err) {
      console.error('Failed to load conversations:', err);
    }
  }, [coachClientId]);

  useEffect(() => {
    if (isOpen) {
      loadUsage();
    }
  }, [isOpen, loadUsage]);

  useEffect(() => {
    if (!isOpen && isExpanded) {
      setIsExpanded(false);
    }
  }, [isOpen, isExpanded]);

  useEffect(() => {
    if (showHistory) {
      loadConversations();
    }
  }, [showHistory, loadConversations]);

  // Focus input when panel opens
  useEffect(() => {
    if (!isOpen || !inputRef.current) return;

    // On iOS/mobile Safari, auto-focusing can open the keyboard and shift the
    // visual viewport, hiding the panel header/close controls.
    const isMobileViewport = typeof window !== 'undefined' &&
      window.matchMedia('(max-width: 768px), (pointer: coarse)').matches;

    if (isMobileViewport) return;

    const focusTimer = window.setTimeout(() => inputRef.current?.focus(), 100);
    return () => window.clearTimeout(focusTimer);
  }, [isOpen]);

  const handleNewConversation = useCallback(() => {
    setActiveConversationId(null);
    activeConversationIdRef.current = null;
    setMessages([]);
    setInputValue('');
    setShowHistory(false);

    const isMobileViewport = typeof window !== 'undefined' &&
      window.matchMedia('(max-width: 768px), (pointer: coarse)').matches;
    if (!isMobileViewport) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [setMessages]);

  const handleLoadConversation = useCallback(async (convId) => {
    try {
      const data = await getConversation(convId);
      setActiveConversationId(convId);
      activeConversationIdRef.current = convId;
      // Convert stored messages to UIMessage format (v6: parts array)
      const chatMessages = (data.messages || [])
        .filter(m => m.role === 'user' || m.role === 'assistant')
        .map(m => ({
          id: m._id,
          role: m.role,
          parts: [{ type: 'text', text: m.content || '' }]
        }));
      setMessages(chatMessages);
      setShowHistory(false);
    } catch (err) {
      console.error('Failed to load conversation:', err);
    }
  }, [setMessages]);

  const handleDeleteConversation = useCallback(async (convId, e) => {
    e.stopPropagation();
    try {
      await deleteConversation(convId);
      setConversations(prev => prev.filter(c => c._id !== convId));
      if (activeConversationId === convId) {
        handleNewConversation();
      }
    } catch (err) {
      console.error('Failed to delete conversation:', err);
    }
  }, [activeConversationId, handleNewConversation]);

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (atLimit || !inputValue.trim() || isLoading) return;
    // Stop listening if user submits while mic is on
    if (isListening) stopListening();
    sendMessage({ text: inputValue.trim() });
    setInputValue('');
  };

  const startListening = useCallback(() => {
    if (!speechSupported) return;
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.lang = 'en-US';
    recognition.interimResults = true;
    recognition.continuous = true;

    // Track the text that was in the input before we started listening
    const prefixRef = { current: inputValue };

    recognition.onresult = (event) => {
      let final = '';
      let interim = '';
      for (let i = 0; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          final += transcript;
        } else {
          interim += transcript;
        }
      }
      const prefix = prefixRef.current;
      const sep = prefix && !prefix.endsWith(' ') ? ' ' : '';
      setInputValue((prefix + sep + final + interim).trimStart());
    };

    recognition.onend = () => {
      setIsListening(false);
      recognitionRef.current = null;
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      setIsListening(false);
      recognitionRef.current = null;
    };

    recognitionRef.current = recognition;
    recognition.start();
    setIsListening(true);
  }, [speechSupported, inputValue]);

  const stopListening = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    setIsListening(false);
  }, []);

  const toggleListening = useCallback(() => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  }, [isListening, startListening, stopListening]);

  const toggleExpanded = useCallback(() => {
    const isMobileViewport = typeof window !== 'undefined' &&
      window.matchMedia('(max-width: 768px), (pointer: coarse)').matches;

    if (isMobileViewport) {
      return;
    }

    setIsExpanded((prev) => !prev);
  }, []);

  const handleOpenPanel = useCallback(() => {
    const isMobileViewport = typeof window !== 'undefined' &&
      window.matchMedia('(max-width: 768px), (pointer: coarse)').matches;

    setIsExpanded(!isMobileViewport);
    setIsOpen(true);
  }, []);

  // Allow other parts of the app (e.g. onboarding CTAs) to open the coach.
  useEffect(() => {
    const open = () => handleOpenPanel();
    window.addEventListener('open-ai-coach', open);
    return () => window.removeEventListener('open-ai-coach', open);
  }, [handleOpenPanel]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  const usagePercent = usage ? Math.round((usage.messagesUsed / usage.messagesLimit) * 100) : 0;

  // Upgrade-funnel impressions. Effects fire once per false→true transition,
  // and must live above the early return below (hooks can't be conditional).
  const limitPromptVisible = isOpen && atLimit;
  const nudgeVisible =
    isOpen && !!usage && usage.userType !== 'admin' && usagePercent >= 70 && usagePercent < 100;
  useEffect(() => {
    if (limitPromptVisible) trackUpgradePromptShown('ai_coach_limit');
  }, [limitPromptVisible]);
  useEffect(() => {
    if (nudgeVisible) trackUpgradePromptShown('ai_coach_nudge');
  }, [nudgeVisible]);

  if (!isOpen) {
    return (
      <button
        className="ai-coach-fab"
        onClick={handleOpenPanel}
        title="AI Coach"
        aria-label="Open AI Coach"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 2a7 7 0 0 1 7 7c0 2.38-1.19 4.47-3 5.74V17a2 2 0 0 1-2 2H10a2 2 0 0 1-2-2v-2.26C6.19 13.47 5 11.38 5 9a7 7 0 0 1 7-7z"/>
          <path d="M10 21h4"/>
          <path d="M9 9h.01"/>
          <path d="M15 9h.01"/>
          <path d="M9.5 13a3.5 3.5 0 0 0 5 0"/>
        </svg>
      </button>
    );
  }

  return (
    <div className={`ai-coach-panel ${isExpanded ? 'expanded' : ''}`}>
      {/* Header */}
      <div className="ai-coach-header">
        <div className="ai-coach-header-left">
          <button
            className="ai-coach-history-btn"
            onClick={() => setShowHistory(!showHistory)}
            title="Conversation history"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 8v4l3 3"/>
              <circle cx="12" cy="12" r="10"/>
            </svg>
          </button>
          <span className="ai-coach-title">AI Coach</span>
        </div>
        <div className="ai-coach-header-right">
          <button
            className="ai-coach-expand-btn"
            onClick={toggleExpanded}
            title={isExpanded ? 'Restore size' : 'Expand panel'}
            aria-label={isExpanded ? 'Restore AI Coach size' : 'Expand AI Coach panel'}
          >
            {isExpanded ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="14 3 14 10 21 10"/>
                <polyline points="10 21 10 14 3 14"/>
                <line x1="14" y1="10" x2="21" y2="3"/>
                <line x1="3" y1="21" x2="10" y2="14"/>
              </svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="21 14 14 14 14 21"/>
                <polyline points="3 10 10 10 10 3"/>
                <line x1="14" y1="14" x2="21" y2="21"/>
                <line x1="3" y1="3" x2="10" y2="10"/>
              </svg>
            )}
          </button>
          <button
            className="ai-coach-new-btn"
            onClick={handleNewConversation}
            title="New conversation"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"/>
              <line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
          </button>
          <button
            className="ai-coach-close-btn"
            onClick={() => setIsOpen(false)}
            title="Close"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
      </div>

      {/* History sidebar */}
      {showHistory && (
        <div className="ai-coach-history">
          <div className="ai-coach-history-header">
            <span>Conversations</span>
          </div>
          <div className="ai-coach-history-list">
            {conversations.length === 0 ? (
              <div className="ai-coach-history-empty">No conversations yet</div>
            ) : (
              conversations.map(conv => (
                <div
                  key={conv._id}
                  className={`ai-coach-history-item ${activeConversationId === conv._id ? 'active' : ''}`}
                  onClick={() => handleLoadConversation(conv._id)}
                >
                  <div className="ai-coach-history-item-title">{conv.title}</div>
                  <div className="ai-coach-history-item-meta">
                    {new Date(conv.lastMessageAt).toLocaleDateString()}
                    <button
                      className="ai-coach-history-delete"
                      onClick={(e) => handleDeleteConversation(conv._id, e)}
                      title="Delete"
                    >
                      ×
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="ai-coach-messages">
        {messages.length === 0 && (
          <div className="ai-coach-welcome">
            <div className="ai-coach-welcome-icon">🏋️‍♀️🥗</div>
            <h3>AI Fitness & Nutrition Coach</h3>
            <p>Ask me about exercises, nutrition, workouts, meal planning, or anything fitness-related!</p>
            <div className="ai-coach-suggestions">
              <button onClick={() => setInputValue('What can I cook with chicken, rice, and spinach?')}>
                What can I cook with these ingredients?
              </button>
              <button onClick={() => setInputValue('Create a chicken and marinara recipe and save it to my menus.')}>
                Create and save a recipe to My Menus
              </button>
              <button onClick={() => setInputValue('Suggest a beginner full-body workout')}>
                Suggest a beginner workout
              </button>
            </div>
          </div>
        )}
        {messages.map((msg) => (
          <div key={msg.id} className={`ai-coach-message ai-coach-message-${msg.role}`}>
            <div className={`ai-coach-bubble ai-coach-bubble-${msg.role}`}>
              {msg.parts ? (
                msg.parts.map((part, i) => renderPart(part, i))
              ) : (
                <div className="ai-coach-text">{formatMessage(msg.content || '')}</div>
              )}
            </div>
          </div>
        ))}
        {isLoading && (
          <div className="ai-coach-message ai-coach-message-assistant">
            <div className="ai-coach-bubble ai-coach-bubble-assistant">
              <div className="ai-coach-typing">
                <span></span><span></span><span></span>
              </div>
            </div>
          </div>
        )}
        {error && (
          <div className="ai-coach-error">
            {atLimit ? 'You\'ve reached your monthly AI Coach limit.' : 'Something went wrong. Please try again.'}
            {atLimit && (
              <a href={usage?.userType === 'affiliate' ? '/pricing#pricing-creator' : usage?.userType === 'business' ? '/pricing#pricing-business' : '/pricing#pricing-personal'} className="ai-coach-upgrade-link" onClick={() => trackUpgradePromptClick('ai_coach_limit')}>Upgrade Plan</a>
            )}
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Footer with usage + input */}
      <div className="ai-coach-footer">
        {usage && usage.userType !== 'admin' && (
          <div className="ai-coach-usage">
            <div className="ai-coach-usage-bar">
              <div
                className={`ai-coach-usage-fill ${usagePercent >= 80 ? 'warning' : ''} ${usagePercent >= 100 ? 'full' : ''}`}
                style={{ width: `${Math.min(usagePercent, 100)}%` }}
              />
            </div>
            <span className="ai-coach-usage-text">
              {usage.messagesUsed}/{usage.messagesLimit} messages
              {usagePercent >= 70 && usagePercent < 100 && (
                <a href={usage?.userType === 'affiliate' ? '/pricing#pricing-creator' : '/pricing#pricing-personal'} className="ai-coach-nudge-link" onClick={() => trackUpgradePromptClick('ai_coach_nudge')}> — Upgrade for more</a>
              )}
            </span>
          </div>
        )}
        <form onSubmit={handleFormSubmit} className="ai-coach-input-form">
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder={atLimit ? 'Message limit reached — upgrade to continue' : 'Ask your AI coach...'}
            disabled={isLoading || atLimit}
            className="ai-coach-input"
          />
          {speechSupported && (
            <button
              type="button"
              onClick={toggleListening}
              disabled={atLimit}
              className={`ai-coach-mic-btn ${isListening ? 'listening' : ''}`}
              title={isListening ? 'Stop listening' : 'Voice input'}
              aria-label={isListening ? 'Stop voice input' : 'Start voice input'}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="9" y="1" width="6" height="12" rx="3"/>
                <path d="M19 10v1a7 7 0 0 1-14 0v-1"/>
                <line x1="12" y1="19" x2="12" y2="23"/>
                <line x1="8" y1="23" x2="16" y2="23"/>
              </svg>
            </button>
          )}
          <button
            type="submit"
            disabled={isLoading || !inputValue.trim() || atLimit}
            className="ai-coach-send-btn"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="22" y1="2" x2="11" y2="13"/>
              <polygon points="22 2 15 22 11 13 2 9 22 2"/>
            </svg>
          </button>
        </form>
      </div>
    </div>
  );
}

/** Format tool names for display */
function formatToolName(name) {
  return name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

/**
 * Human-friendly present-tense label for a tool while it's running.
 * Falls back to a generic "Using {Tool Name}".
 */
const TOOL_RUNNING_LABELS = {
  search_exercises: 'Searching exercises',
  search_foods: 'Looking up foods',
  search_recipes: 'Searching recipes',
  search_programs: 'Searching programs',
  search_workouts: 'Searching workouts',
  get_my_programs: 'Loading your programs',
  get_program_detail: 'Reading program details',
  get_my_workouts: 'Loading your workouts',
  get_workout_detail: 'Reading workout details',
  get_workout_sessions: 'Reviewing your sessions',
  get_session_detail: 'Reading session details',
  get_progression_summary: 'Analyzing your progression',
  get_nutrition_goals: 'Checking nutrition goals',
  calculate_nutrition: 'Calculating nutrition',
  create_workout: 'Creating a workout',
  create_program: 'Building your program',
  create_personal_recipe: 'Saving your recipe',
  create_nutrition_goal: 'Setting nutrition goals',
  schedule_workout: 'Scheduling your workout',
  list_clients: 'Loading your clients',
  get_client_profile: 'Reading client profile'
};

function toolRunningLabel(toolName) {
  return TOOL_RUNNING_LABELS[toolName] || `Using ${formatToolName(toolName)}`;
}

/**
 * Render a single UIMessage part (AI SDK v6 shapes):
 *  - text
 *  - reasoning (model "thinking")
 *  - tool-<name> / dynamic-tool with state input-streaming|input-available|output-available|output-error
 *  - step-start (rendered as a subtle divider between turns)
 */
function renderPart(part, i) {
  if (!part || !part.type) return null;

  if (part.type === 'text') {
    return <div key={i} className="ai-coach-text">{formatMessage(part.text)}</div>;
  }

  if (part.type === 'reasoning') {
    const text = (part.text || '').trim();
    if (!text) return null;
    return (
      <div key={i} className="ai-coach-reasoning">
        <span className="ai-coach-reasoning-icon">💭</span>
        <span className="ai-coach-reasoning-text">{text}</span>
      </div>
    );
  }

  // v6 tool parts: static tools are "tool-<name>", dynamic tools are "dynamic-tool".
  const isToolPart = part.type === 'dynamic-tool' || part.type.startsWith('tool-');
  if (isToolPart) {
    const toolName = part.type === 'dynamic-tool'
      ? (part.toolName || 'tool')
      : part.type.slice('tool-'.length);
    const state = part.state;
    const errored = state === 'output-error'
      || (state === 'output-available' && part.output && part.output.success === false);
    const done = state === 'output-available';
    const running = state === 'input-streaming' || state === 'input-available';

    let statusIcon = '🔧';
    let statusEl = null;
    if (errored) {
      statusIcon = '⚠️';
      statusEl = <span className="ai-coach-tool-error">couldn’t complete</span>;
    } else if (done) {
      statusEl = <span className="ai-coach-tool-done">✓</span>;
    } else if (running) {
      statusEl = <span className="ai-coach-tool-spinner" aria-hidden="true" />;
    }

    return (
      <div key={i} className={`ai-coach-tool-call ${errored ? 'errored' : ''}`}>
        <span className="ai-coach-tool-icon">{statusIcon}</span>
        <span className="ai-coach-tool-name">
          {running ? `${toolRunningLabel(toolName)}…` : formatToolName(toolName)}
        </span>
        {statusEl}
      </div>
    );
  }

  return null;
}

/** Simple message formatter: handle markdown-like bold and line breaks */
function formatMessage(text) {
  if (!text) return null;
  
  return text.split('\n').map((line, i) => {
    // Bold
    const parts = line.split(/\*\*(.*?)\*\*/g);
    const formatted = parts.map((part, j) => 
      j % 2 === 1 ? <strong key={j}>{part}</strong> : part
    );
    
    return (
      <React.Fragment key={i}>
        {i > 0 && <br />}
        {formatted}
      </React.Fragment>
    );
  });
}

export default AIChatPanel;
